import './header-sticky.js';

function themeDocumentScrollY() {
  if (document.documentElement.hasAttribute('scroll-lock')) {
    const bodyTop = document.body.style.top;
    if (bodyTop) {
      const locked = parseInt(bodyTop, 10);
      if (!Number.isNaN(locked) && locked <= 0) {
        return Math.abs(locked);
      }
    }
  }

  const a = window.pageYOffset;
  const se = document.scrollingElement || document.documentElement;
  const b = se ? se.scrollTop : 0;
  const c = document.body ? document.body.scrollTop : 0;
  const ay = typeof a === 'number' && !Number.isNaN(a) ? a : 0;
  return Math.max(ay, b || 0, c || 0);
}

window.__themeDocumentScrollY = themeDocumentScrollY;

class ThemeHeader {
  constructor() {
    this.isCartFetching = false;
    this.pendingCartUpdate = false;
    this.cachedElements = new Map();
    this.cartUpdateScheduled = false;
    this.lastItemCount = null;
    this.cartCache = null;
    this.cartCacheTime = 0;
    this.cartCacheTTL = 5000;
    this.topBannerDock = {
      banner: null,
      parent: null,
      nextSibling: null,
      promoSlot: null,
      mobileMenu: null,
      mobileMenuWrapper: null
    };
    this.topBannerDesktopDock = {
      banner: null,
      parent: null,
      nextSibling: null,
      promoSlot: null,
      menu: null
    };
    this.topBannerHome = {
      parent: null,
      nextSibling: null
    };

    this.init();
  }

  getElement(selector) {
    if (!this.cachedElements.has(selector)) {
      this.cachedElements.set(selector, document.querySelector(selector));
    }
    return this.cachedElements.get(selector);
  }

  getElements(selector) {
    return Array.from(document.querySelectorAll(selector));
  }

  getActiveHeader() {
    return document.querySelector('.editorial-banner__header-layer .theme-header--embedded') ||
      document.querySelector('.theme-header');
  }

  getActiveDesktopMenu() {
    return document.querySelector('.editorial-banner__header-layer .block_navigation_menu') ||
      document.querySelector('.block_navigation_menu');
  }

  getActiveDesktopPromoSlot() {
    return document.querySelector('.editorial-banner__header-layer [data-theme-desktop-promo-slot]') ||
      document.querySelector('[data-theme-desktop-promo-slot]');
  }

  getActiveMobileHeader() {
    return document.querySelector('.editorial-banner__header-layer .block_navigation_header_mob') ||
      document.querySelector('.block_navigation_header_mob');
  }

  getActiveMobileWrapper() {
    return document.querySelector('.editorial-banner__header-layer .theme-mobile-nav--embedded') ||
      document.querySelector('.theme-mobile-nav');
  }

  bootstrapEmbeddedMobileHeader() {
    if (window.__themeEmbeddedHeaderBootstrapped) return;

    const mobileMenu = this.getActiveMobileHeader();
    const mobileMenuWrapper = this.getActiveMobileWrapper();
    if (!mobileMenuWrapper?.classList.contains('theme-mobile-nav--embedded')) return;

    window.__themeEmbeddedHeaderBootstrapped = true;
    mobileMenu?.classList.add('block_navigation_header_mob--sticky');
    mobileMenuWrapper.classList.add('theme-mobile-nav--sticky');
    document.body.classList.add('theme-mobile-nav-sticky');
    this.queueTopBannerDockSync();
  }

  init() {
    this.initCartUpdateEvents();
    this.syncTopBannerHeight();
    this.syncHeroOverlayState();
    this.bindHeroOverlayEvents();
    this.bootstrapEmbeddedMobileHeader();
    this.initStickyHeader();
    this.initMenuHoverEffects();
    this.bindMobileStickyEvents();
    this.bindDesktopStickyEvents();
    this.cacheTopBannerHome(this.getTopBannerSource());
    this.queueTopBannerDockSync();
    window.requestAnimationFrame(() => {
      this.refreshHeaderGroupHeight();
    });

    window.setTimeout(() => {
      if (typeof window.applyMobileHeaderSpacerHeight === 'function') {
        window.applyMobileHeaderSpacerHeight(window.measureMobileHeaderSpacerHeight());
      } else {
        this.refreshHeaderGroupHeight();
      }
    }, 250);

    this.scheduleInitialCartCountUpdate();
  }

  scheduleInitialCartCountUpdate() {
    const run = () => {
      const isHome = document.body?.classList.contains('template-index');
      if (isHome) {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(() => {
            this.updateCartCount();
          }, { timeout: 4000 });
          return;
        }

        window.setTimeout(() => this.updateCartCount(), 1800);
        return;
      }

      this.updateCartCount();
    };

    if (document.readyState === 'complete') {
      run();
      return;
    }

    window.addEventListener('load', () => {
      window.setTimeout(run, 100);
    }, { once: true });
  }

  syncHeroOverlayState() {
    const header = this.getActiveHeader();
    const firstSection = document.querySelector('#MainContent > .shopify-section:first-child');
    const overlayEnabled = header?.dataset.overlayFirstHero === 'true';
    const hasOverlayBannerFirst = Boolean(
      firstSection?.querySelector(
        '.hero, .editorial-banner, [data-editorial-banner]'
      )
    );
    const hasEmbeddedHeader = Boolean(
      firstSection?.querySelector('[data-editorial-embedded-header]')
    );

    document.body.classList.toggle(
      'theme-first-section-hero',
      Boolean(overlayEnabled && hasOverlayBannerFirst)
    );
    document.body.classList.toggle(
      'theme-editorial-embedded-header-active',
      hasEmbeddedHeader
    );

    if (hasEmbeddedHeader) {
      this.refreshHeaderGroupHeight();
    }

    if (!overlayEnabled || !hasOverlayBannerFirst) {
      document.body.classList.remove('theme-header-overlay-active');
      return;
    }

    if (overlayEnabled && hasOverlayBannerFirst && themeDocumentScrollY() <= 1) {
      document.body.classList.add('theme-header-overlay-active');
    }
  }

  syncTopBannerHeight() {
    const dockedBanner = document.querySelector('.top-banner.top-banner--docked');
    const topBanner = dockedBanner ||
      document.querySelector('#header-group .top-banner:not(.top-banner--index-deferred)') ||
      document.querySelector('#header-group .top-banner, .top-banner');
    let height = 0;

    if (topBanner && this.isTopBannerVisible(topBanner)) {
      const style = window.getComputedStyle(topBanner);
      if (style.display !== 'none' && style.visibility !== 'hidden') {
        height = topBanner.offsetHeight;
      }
    }

    document.documentElement.style.setProperty('--top-banner-height', `${height}px`);
  }

  shouldDockMobileTopBanner(isSticky, mobileMenu) {
    if (!mobileMenu || window.innerWidth > 1024) return false;

    const sourceBanner = this.getTopBannerSource();
    const mobileMenuWrapper = mobileMenu.closest('.theme-mobile-nav');
    const promoSlot = mobileMenu.querySelector('[data-theme-mob-promo-slot]');
    const overlayActive = document.body.classList.contains('theme-header-overlay-active');
    const wantsBanner = Boolean(isSticky || overlayActive);

    return Boolean(
      wantsBanner &&
      sourceBanner &&
      mobileMenuWrapper &&
      promoSlot &&
      this.canDockTopBanner(sourceBanner)
    );
  }

  getTopBannerSource() {
    return document.querySelector('#header-group [data-top-banner]');
  }

  cacheTopBannerHome(banner) {
    if (!banner || banner.classList.contains('top-banner--docked')) return;

    this.topBannerHome.parent = banner.parentElement;
    this.topBannerHome.nextSibling = banner.nextSibling;
  }

  getTopBannerHomeParent(banner) {
    if (this.topBannerHome.parent && document.contains(this.topBannerHome.parent)) {
      return this.topBannerHome.parent;
    }

    const topBannerSection = document.querySelector('#header-group .shopify-section:has([data-top-banner])');
    if (topBannerSection) {
      return topBannerSection;
    }

    return banner?.parentElement || null;
  }

  isBannerInDesktopPromoSlot(banner) {
    if (!banner) return false;
    return Boolean(banner.closest('[data-theme-nav-promo-slot]'));
  }

  isBannerInMobilePromoSlot(banner) {
    if (!banner) return false;
    return Boolean(banner.closest('[data-theme-mob-promo-slot]'));
  }

  isTopBannerVisible(banner) {
    if (!banner) return false;
    if (
      banner.classList.contains('top-banner--index-deferred') &&
      !banner.classList.contains('top-banner--docked')
    ) {
      return false;
    }
    const style = window.getComputedStyle(banner);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  canDockTopBanner(banner) {
    if (!banner) return false;
    if (
      banner.classList.contains('top-banner--index-deferred') &&
      !banner.classList.contains('top-banner--docked')
    ) {
      return true;
    }
    return this.isTopBannerVisible(banner);
  }

  parkTopBannerPlaceholder(dock, banner) {
    if (document.body.classList.contains('theme-first-section-hero')) {
      if (dock.placeholder instanceof HTMLElement) {
        dock.placeholder.remove();
        dock.placeholder = null;
      }
      document.querySelectorAll('.top-banner-dock-placeholder').forEach((el) => el.remove());
      return;
    }

    if (window.innerWidth <= 1024) {
      if (dock.placeholder instanceof HTMLElement) {
        dock.placeholder.remove();
        dock.placeholder = null;
      }
      document.querySelectorAll('.top-banner-dock-placeholder').forEach((el) => el.remove());
      return;
    }

    if (
      document.querySelector('.theme-header--sticky') ||
      document.querySelector('.block_navigation_menu--sticky')
    ) {
      if (dock.placeholder instanceof HTMLElement) {
        dock.placeholder.remove();
        dock.placeholder = null;
      }
      document.querySelectorAll('.top-banner-dock-placeholder').forEach((el) => el.remove());
      return;
    }
    if (!(banner instanceof HTMLElement)) return;
    if (dock.placeholder instanceof HTMLElement && dock.placeholder.isConnected) return;

    const height = Math.max(
      Math.round(banner.getBoundingClientRect().height || banner.offsetHeight || 0),
      32
    );

    const placeholder = document.createElement('div');
    placeholder.className = 'top-banner-dock-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.style.height = `${height}px`;
    dock.placeholder = placeholder;

    if (banner.parentNode && !banner.classList.contains('top-banner--docked')) {
      banner.parentNode.insertBefore(placeholder, banner);
      return;
    }

    const parent = dock.parent && document.contains(dock.parent)
      ? dock.parent
      : this.topBannerHome.parent;

    if (!(parent instanceof HTMLElement)) return;

    if (dock.nextSibling && dock.nextSibling.parentNode === parent) {
      parent.insertBefore(placeholder, dock.nextSibling);
    } else {
      parent.appendChild(placeholder);
    }
  }

  restoreTopBannerDock(dock) {
    if (!dock.banner) return;

    const banner = dock.banner;
    const placeholder = dock.placeholder;

    banner.classList.remove('top-banner--docked');

    if (placeholder instanceof HTMLElement && placeholder.isConnected) {
      placeholder.replaceWith(banner);
    } else {
      const restoreParent = dock.parent && document.contains(dock.parent)
        ? dock.parent
        : this.getTopBannerHomeParent(banner);

      if (!restoreParent) {
        dock.banner = null;
        dock.parent = null;
        dock.nextSibling = null;
        dock.promoSlot = null;
        dock.placeholder = null;
        return;
      }

      if (dock.nextSibling && dock.nextSibling.parentNode === restoreParent) {
        restoreParent.insertBefore(banner, dock.nextSibling);
      } else if (this.topBannerHome.nextSibling && this.topBannerHome.nextSibling.parentNode === restoreParent) {
        restoreParent.insertBefore(banner, this.topBannerHome.nextSibling);
      } else {
        restoreParent.appendChild(banner);
      }
    }

    dock.banner = null;
    dock.parent = null;
    dock.nextSibling = null;
    dock.promoSlot = null;
    dock.placeholder = null;
  }

  reconcileDesktopTopBannerDock(isSticky, menu) {
    const sourceBanner = this.getTopBannerSource();
    if (!sourceBanner || !menu) return;

    const dock = this.topBannerDesktopDock;
    const bannerInPromoSlot = this.isBannerInDesktopPromoSlot(sourceBanner);

    if (!isSticky) {
      if (dock.banner || bannerInPromoSlot) {
        if (!dock.banner && bannerInPromoSlot) {
          dock.banner = sourceBanner;
          dock.promoSlot = sourceBanner.closest('[data-theme-nav-promo-slot]');
          dock.menu = menu;
        }
        this.restoreDesktopTopBannerDock();
      }
      return;
    }

    if (isSticky && bannerInPromoSlot && dock.banner !== sourceBanner) {
      dock.banner = sourceBanner;
      dock.promoSlot = sourceBanner.closest('[data-theme-nav-promo-slot]');
      dock.menu = menu;
      menu.classList.add('has-top-banner');
      dock.promoSlot?.classList.add('has-docked-top-banner');
    }
  }

  restoreMobileTopBannerDock() {
    const dock = this.topBannerDock;
    if (!dock.banner) return;

    const mobileMenu = dock.mobileMenu;
    const mobileMenuWrapper = dock.mobileMenuWrapper;
    const promoSlot = dock.promoSlot;

    this.restoreTopBannerDock(dock);

    if (mobileMenu) {
      mobileMenu.classList.remove('has-top-banner');
    }
    if (mobileMenuWrapper) {
      mobileMenuWrapper.classList.remove('has-top-banner');
    }
    if (promoSlot) {
      promoSlot.classList.remove('has-docked-top-banner');
    }

    dock.mobileMenu = null;
    dock.mobileMenuWrapper = null;
  }

  restoreDesktopTopBannerDock() {
    const dock = this.topBannerDesktopDock;
    if (!dock.banner) return;

    const menu = dock.menu;
    const header = dock.header;
    const promoSlot = dock.promoSlot;

    this.restoreTopBannerDock(dock);

    if (menu) {
      menu.classList.remove('has-top-banner');
    }
    if (header) {
      header.classList.remove('has-top-banner');
    }
    if (promoSlot) {
      promoSlot.classList.remove('has-docked-top-banner');
    }

    dock.menu = null;
    dock.header = null;
  }

  queueTopBannerDockSync() {
    if (this._topBannerDockRaf) {
      window.cancelAnimationFrame(this._topBannerDockRaf);
    }

    this._topBannerDockRaf = window.requestAnimationFrame(() => {
      this._topBannerDockRaf = null;

      if (window.innerWidth <= 1024) {
        const mobileMenu = this.getActiveMobileHeader();
        const mobileMenuWrapper = this.getActiveMobileWrapper();
        const isSticky = Boolean(mobileMenuWrapper?.classList.contains('theme-mobile-nav--sticky'));
        const overlayActive = document.body.classList.contains('theme-header-overlay-active');
        this.syncMobileTopBannerDock(isSticky || overlayActive, mobileMenu);
        return;
      }

      const menu = this.getActiveDesktopMenu();
      this.syncMobileTopBannerDock(false, this.getActiveMobileHeader());
      this.syncDesktopTopBannerDock(Boolean(menu?.classList.contains('block_navigation_menu--sticky')), menu);
    });
  }

  syncMobileTopBannerDock(isSticky, mobileMenu) {
    const sourceBanner = this.getTopBannerSource();
    if (!sourceBanner || !mobileMenu) return;

    const mobileMenuWrapper = mobileMenu.closest('.theme-mobile-nav');
    const promoSlot = mobileMenu.querySelector('[data-theme-mob-promo-slot]');
    const dock = this.topBannerDock;
    const bootstrapDock = window.__themeTopBannerDockSnapshot;

    if (
      !dock.banner &&
      bootstrapDock &&
      bootstrapDock.banner === sourceBanner &&
      bootstrapDock.mobileMenu === mobileMenu
    ) {
      dock.parent = bootstrapDock.parent;
      dock.nextSibling = bootstrapDock.nextSibling;
      dock.banner = bootstrapDock.banner;
      dock.promoSlot = bootstrapDock.promoSlot;
      dock.mobileMenu = bootstrapDock.mobileMenu;
      dock.mobileMenuWrapper = bootstrapDock.mobileMenuWrapper;
      dock.placeholder = bootstrapDock.placeholder;
    }

    if (
      dock.banner &&
      dock.mobileMenu &&
      dock.mobileMenu !== mobileMenu
    ) {
      this.restoreMobileTopBannerDock();
    }

    const shouldDockBanner = this.shouldDockMobileTopBanner(isSticky, mobileMenu);

    if (shouldDockBanner) {
      if (this.topBannerDesktopDock.banner) {
        this.restoreDesktopTopBannerDock();
      }

      if (dock.banner === sourceBanner && dock.promoSlot === promoSlot) {
        this.parkTopBannerPlaceholder(dock, sourceBanner);
        this.syncTopBannerHeight();
        return;
      }

      dock.parent = sourceBanner.parentElement;
      dock.nextSibling = sourceBanner.nextSibling;
      dock.banner = sourceBanner;
      dock.promoSlot = promoSlot;
      dock.mobileMenu = mobileMenu;
      dock.mobileMenuWrapper = mobileMenuWrapper;

      this.cacheTopBannerHome(sourceBanner);
      this.parkTopBannerPlaceholder(dock, sourceBanner);

      sourceBanner.classList.add('top-banner--docked');
      promoSlot.appendChild(sourceBanner);
      mobileMenu.classList.add('has-top-banner');
      mobileMenuWrapper.classList.add('has-top-banner');
      promoSlot.classList.add('has-docked-top-banner');

      if (window.topBannerInstance && typeof window.topBannerInstance.initBanner === 'function') {
        window.topBannerInstance.initBanner();
      }
    } else if (dock.banner && dock.parent) {
      this.restoreMobileTopBannerDock();
    }

    this.syncTopBannerHeight();

    if (typeof window.syncMobileStickyHeights === 'function') {
      window.requestAnimationFrame(() => {
        window.syncMobileStickyHeights(mobileMenu, mobileMenuWrapper);
        this.refreshHeaderGroupHeight();
      });
    } else {
      this.refreshHeaderGroupHeight();
    }
  }

  syncDesktopTopBannerDock(isSticky, menu) {
    if (window.innerWidth > 1024) {
      this.reconcileDesktopTopBannerDock(false, menu);
      return;
    }

    const sourceBanner = this.getTopBannerSource();
    if (!sourceBanner || !menu) return;

    const header = this.getActiveHeader();
    const overlayActive = document.body.classList.contains('theme-header-overlay-active');
    const wantsDock = Boolean(isSticky || overlayActive);

    this.reconcileDesktopTopBannerDock(wantsDock, menu);

    const promoSlot = this.getActiveDesktopPromoSlot();
    const dock = this.topBannerDesktopDock;
    const shouldDockBanner = Boolean(
      wantsDock &&
      promoSlot &&
      header &&
      window.innerWidth > 1024 &&
      this.canDockTopBanner(sourceBanner)
    );

    if (shouldDockBanner) {
      if (this.topBannerDock.banner) {
        this.restoreMobileTopBannerDock();
      }

      if (dock.banner === sourceBanner && dock.promoSlot === promoSlot) {
        this.parkTopBannerPlaceholder(dock, sourceBanner);
        this.syncTopBannerHeight();
        this.refreshHeaderGroupHeight();
        if (isSticky && typeof window.refreshDesktopStickySpacer === 'function') {
          window.requestAnimationFrame(() => {
            window.refreshDesktopStickySpacer(header, menu);
          });
        }
        return;
      }

      this.cacheTopBannerHome(sourceBanner);
      dock.parent = sourceBanner.parentElement;
      dock.nextSibling = sourceBanner.nextSibling;
      dock.banner = sourceBanner;
      dock.promoSlot = promoSlot;
      dock.menu = menu;
      dock.header = header;

      this.parkTopBannerPlaceholder(dock, sourceBanner);

      sourceBanner.classList.add('top-banner--docked');
      promoSlot.appendChild(sourceBanner);
      header.classList.add('has-top-banner');
      promoSlot.classList.add('has-docked-top-banner');

      if (window.topBannerInstance && typeof window.topBannerInstance.initBanner === 'function') {
        window.topBannerInstance.initBanner();
      }
    } else if (dock.banner || this.isBannerInDesktopPromoSlot(sourceBanner)) {
      this.restoreDesktopTopBannerDock();
    }

    this.syncTopBannerHeight();
    this.refreshHeaderGroupHeight();

    if (isSticky && typeof window.refreshDesktopStickySpacer === 'function') {
      window.requestAnimationFrame(() => {
        window.refreshDesktopStickySpacer(header, menu);
      });
    }
  }

  bindMobileStickyEvents() {
    document.addEventListener('theme:mobile-sticky-change', (event) => {
      const detail = event.detail || {};
      this.syncMobileTopBannerDock(Boolean(detail.isSticky), detail.mobileMenu || this.getActiveMobileHeader());
      this.refreshHeaderGroupHeight();
    });
  }

  bindDesktopStickyEvents() {
    document.addEventListener('theme:desktop-sticky-change', (event) => {
      const detail = event.detail || {};
      const menu = detail.menu || this.getActiveDesktopMenu();
      const isSticky = Boolean(detail.isSticky);
      this.syncDesktopTopBannerDock(isSticky, menu);
      if (typeof window.syncDesktopStickyIsland === 'function') {
        window.syncDesktopStickyIsland(menu, isSticky);
        window.requestAnimationFrame(() => {
          window.syncDesktopStickyIsland(menu, isSticky);
        });
      }
    });

    window.addEventListener('resize', () => {
      this.queueTopBannerDockSync();
    });
  }

  refreshHeaderGroupHeight() {
    this.syncTopBannerHeight();

    const headerGroup = document.querySelector('#header-group');
    const isHeroFirstSection = document.body.classList.contains('theme-first-section-hero');

    if (isHeroFirstSection) {
      document.body.style.setProperty('--header-group-height', '0px');
      if (headerGroup) {
        headerGroup.style.removeProperty('min-height');
      }
      return;
    }

    if (typeof window.syncHeaderGroupSpacer === 'function') {
      window.syncHeaderGroupSpacer(this.getActiveHeader());
      return;
    }

    if (document.body.classList.contains('template-index')) {
      document.body.style.setProperty('--header-group-height', '0px');
      return;
    }

    if (window.innerWidth <= 1024) {
      if (typeof window.applyMobileHeaderSpacerHeight === 'function') {
        window.applyMobileHeaderSpacerHeight(
          typeof window.measureMobileHeaderSpacerHeight === 'function'
            ? window.measureMobileHeaderSpacerHeight()
            : parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mobile-sticky-header-height'))
        );
        return;
      }

      const stackHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--mobile-sticky-header-height')
      );
      if (Number.isFinite(stackHeight) && stackHeight > 0) {
        document.body.style.setProperty('--header-group-height', `${stackHeight}px`);
        const headerGroup = document.getElementById('header-group');
        if (headerGroup) {
          headerGroup.style.setProperty('min-height', `${stackHeight}px`, 'important');
        }
        return;
      }
    }

    if (!headerGroup) return;

    let totalHeight = 0;
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (!(element instanceof HTMLElement)) continue;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      totalHeight += element.offsetHeight;
    }

    document.body.style.setProperty('--header-group-height', `${totalHeight}px`);
    if (totalHeight > 0 && window.innerWidth <= 1024) {
      headerGroup.style.minHeight = `${totalHeight}px`;
    } else if (headerGroup) {
      headerGroup.style.removeProperty('min-height');
    }
  }

  updateHeroOverlayState() {
    const header = this.getActiveHeader();
    const topBanner = document.querySelector('.top-banner.top-banner--docked, #header-group [data-top-banner]');

    if (!header || !document.body.classList.contains('theme-first-section-hero')) {
      document.body.classList.remove('theme-header-overlay-active');
      this._overlayActive = false;
      return;
    }

    const topBannerHeight = topBanner && this.isTopBannerVisible(topBanner) ? topBanner.offsetHeight : 0;
    const headerRow = header.querySelector('.block_navigation_header');
    const menu = header.querySelector('.block_navigation_menu');
    const rowHeight = headerRow instanceof HTMLElement ? headerRow.offsetHeight : 0;
    const menuHeight = menu instanceof HTMLElement && !menu.classList.contains('block_navigation_menu--sticky')
      ? menu.offsetHeight
      : Number.parseInt(menu?.previousElementSibling?.dataset?.lockedHeight || '0', 10) || 54;
    const base = Math.max(rowHeight + menuHeight - topBannerHeight, 0);
    const y = themeDocumentScrollY();
    const was = Boolean(this._overlayActive);
    const isOverlayActive = was ? y <= base + 40 : y <= Math.max(0, base - 24);

    this._overlayActive = isOverlayActive;
    document.body.classList.toggle('theme-header-overlay-active', isOverlayActive);
  }

  bindHeroOverlayEvents() {
    let rafId = null;
    let lastOverlayActive = document.body.classList.contains('theme-header-overlay-active');
    this._overlayActive = lastOverlayActive;

    const syncOverlay = (fromScroll = false) => {
      if (!fromScroll) {
        this.syncHeroOverlayState();
        this.syncTopBannerHeight();
      }

      this.updateHeroOverlayState();

      const overlayActive = document.body.classList.contains('theme-header-overlay-active');
      if (overlayActive === lastOverlayActive) return;

      lastOverlayActive = overlayActive;
      this.syncTopBannerHeight();
      this.queueTopBannerDockSync();
      if (document.body.classList.contains('theme-editorial-embedded-header-active')) {
        this.refreshHeaderGroupHeight();
      }
    };

    const queueOverlaySync = () => {
      if (rafId !== null) return;

      rafId = window.requestAnimationFrame(() => {
        syncOverlay(true);
        rafId = null;
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => syncOverlay(false), { once: true });
    } else {
      syncOverlay(false);
    }

    window.addEventListener('scroll', queueOverlaySync, { passive: true });
    window.addEventListener('resize', () => syncOverlay(false));
    document.addEventListener('shopify:section:load', () => syncOverlay(false));
    document.addEventListener('shopify:section:reorder', () => syncOverlay(false));
  }


  initCartUpdateEvents() {
    let debounceTimer;
    
    const scheduleUpdate = (e) => {
      const itemCount = e?.detail?.cart?.item_count 
        ?? e?.detail?.data?.itemCount 
        ?? e?.detail?.item_count;
      
      if (e && e.detail && itemCount !== undefined) {
        this.applyCartCount(itemCount);
        
        if (e.detail.cart) {
          if (window.cartCache) {
            window.cartCache.invalidate();
          }
          if (window.setCart) {
            window.setCart(e.detail.cart);
          }
        }
        return;
      }
      
      if (window.cartCache) {
        window.cartCache.invalidate();
      }
      
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.updateCartCount();
      }, 100);
    };

    [
      'shopify:cart:update',
      'cart:update',
      'cart:add',
      'cart:change',
      'cart:updated'
    ].forEach(event =>
      document.addEventListener(event, scheduleUpdate, { passive: true })
    );
  }

  updateCartCount(count = null) {
    const textElements = this.getElements('.cart-count__text');
    const containerElements = this.getElements('.cart-count');
    if (!textElements.length || !containerElements.length) return;

    if (count !== null) {
      this.applyCartCount(count);
      return;
    }

    if (window.cartCache) {
      window.cartCache.get()
        .then(cart => {
          if (cart) {
            this.applyCartCount(cart.item_count);
          }
        })
        .catch(() => {});
    } else {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          this.applyCartCount(cart.item_count);
        })
        .catch(() => {});
    }
  }

  applyCartCount(count) {
    const newCount = parseInt(count) || 0;
    
    this.lastItemCount = newCount;

    const textElements = Array.from(document.querySelectorAll('.cart-count__text'));
    const containerElements = Array.from(document.querySelectorAll('.cart-count'));

    textElements.forEach(textEl => {
      if (textEl) {
        textEl.textContent = newCount;
      }
    });

    containerElements.forEach(containerEl => {
      if (!containerEl) return;

      if (newCount > 0) {
        containerEl.style.display = 'flex';
        containerEl.classList.add('cart-has-items');
      } else {
        containerEl.classList.remove('cart-has-items');
      }

      containerEl.style.opacity = '1';
    });
  }

  initStickyHeader() {
    window.mountHeaderSticky(() => ({
      header: this.getActiveHeader(),
      menu: this.getActiveDesktopMenu(),
      mobileMenu: this.getActiveMobileHeader(),
      mobileMenuWrapper: this.getActiveMobileWrapper()
    }));
  }

  getMenuHoverContext(submenu) {
    const collectionImg = submenu.querySelector('.collection_img__image');
    const collectionLink = submenu.querySelector('.collection_img__link');
    if (!collectionImg) return null;

    const defaultImage = collectionImg.dataset.defaultImage || collectionImg.src;
    if (!defaultImage) return null;

    return {
      collectionImg,
      collectionLink,
      defaultImage,
      defaultUrl: collectionLink?.dataset.defaultUrl || collectionLink?.href || '',
      defaultTitle: collectionImg.dataset.defaultTitle || collectionImg.alt || '',
      imageCache: new Map(),
      menuSubItems: submenu.querySelectorAll('[data-menu-sub-item]'),
      state: { currentHoveredItem: null }
    };
  }

  preloadMenuImage(imageCache, imageUrl) {
    if (imageCache.has(imageUrl)) return;

    const img = new Image();
    img.src = imageUrl;
    imageCache.set(imageUrl, img);
  }

  applyMenuHoverImage(context, newSrc, newAlt, newUrl) {
    context.collectionImg.src = newSrc;
    context.collectionImg.alt = newAlt;

    if (context.collectionLink && newUrl) {
      context.collectionLink.href = newUrl;
    }
  }

  changeMenuHoverImage(context, item, newSrc, newAlt, newUrl) {
    if (context.collectionImg.src === newSrc) return;

    const cachedImage = context.imageCache.get(newSrc);
    if (cachedImage && cachedImage.complete) {
      this.applyMenuHoverImage(context, newSrc, newAlt, newUrl);
      return;
    }

    if (!cachedImage) return;

    cachedImage.onload = () => {
      if (context.state.currentHoveredItem !== item && newSrc !== context.defaultImage) return;
      this.applyMenuHoverImage(context, newSrc, newAlt, newUrl);
    };
  }

  hasHoveredMenuItem(menuSubItems) {
    return Array.from(menuSubItems).some(item => item.matches(':hover') || item.querySelector(':hover'));
  }

  resetMenuHoverImage(context) {
    if (context.collectionImg.src === context.defaultImage) return;
    this.applyMenuHoverImage(context, context.defaultImage, context.defaultTitle, context.defaultUrl);
  }

  bindMenuHoverItem(context, item) {
    const imageUrl = item.dataset.collectionImage;
    if (!imageUrl) return;

    const collectionUrl = item.dataset.collectionUrl;
    const title = item.dataset.collectionTitle;
    this.preloadMenuImage(context.imageCache, imageUrl);

    const handleMouseEnter = (e) => {
      e.stopPropagation();
      context.state.currentHoveredItem = item;
      this.changeMenuHoverImage(context, item, imageUrl, title || context.defaultTitle, collectionUrl);
    };

    const handleMouseLeave = (e) => {
      e.stopPropagation();
      if (context.state.currentHoveredItem !== item) return;

      context.state.currentHoveredItem = null;
      if (!this.hasHoveredMenuItem(context.menuSubItems)) {
        this.changeMenuHoverImage(context, item, context.defaultImage, context.defaultTitle, context.defaultUrl);
      }
    };

    item.addEventListener('mouseenter', handleMouseEnter);
    item.addEventListener('mouseleave', handleMouseLeave);

    const submenuLink = item.querySelector('.btn_menu_sub__link');
    if (!submenuLink) return;

    submenuLink.addEventListener('mouseenter', handleMouseEnter);
    submenuLink.addEventListener('mouseleave', handleMouseLeave);
  }

  bindMenuHoverSubmenu(submenu) {
    if (submenu.dataset.menuHoverBound === 'true') return;
    submenu.dataset.menuHoverBound = 'true';

    const context = this.getMenuHoverContext(submenu);
    if (!context) return;

    context.menuSubItems.forEach(item => {
      this.bindMenuHoverItem(context, item);
    });

    submenu.addEventListener('mouseleave', () => {
      context.state.currentHoveredItem = null;
      this.resetMenuHoverImage(context);
    });
  }

  setupMenuHover() {
    this.getElements('.btn_menu__submenu').forEach(submenu => {
      this.bindMenuHoverSubmenu(submenu);
    });
  }

  initMenuHoverEffects() {
    const setupMenuHover = () => {
      this.setupMenuHover();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupMenuHover);
    } else {
      setupMenuHover();
    }

    document.addEventListener('shopify:section:load', setupMenuHover);
  }
}

window.openCart = function () {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer?.open) drawer.open();
  else location.href = '/cart';
};

window.updateCartCount = function (count = null) {
  window.themeHeaderInstance?.updateCartCount(count);
};

function initHeaderOnce() {
  if (!window.themeHeaderInstance) {
    window.themeHeaderInstance = new ThemeHeader();
    return;
  }
  window.themeHeaderInstance.cachedElements.clear();
  window.themeHeaderInstance.bootstrapEmbeddedMobileHeader();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderOnce);
} else {
  initHeaderOnce();
}

document.addEventListener('shopify:section:load', initHeaderOnce);

if (typeof module !== 'undefined') module.exports = ThemeHeader;
